/**
 * CrmSettingsTab - Verwaltung von Kontotypen, Branchen, Beziehungstypen, Kontaktquellen.
 */
import { useState } from 'react';
import {
  useIndustries,
  useCreateIndustry,
  useUpdateIndustry,
  useDeleteIndustry,
  useAccountTypes,
  useCreateAccountType,
  useUpdateAccountType,
  useDeleteAccountType,
  useRelationshipTypes,
  useCreateRelationshipType,
  useUpdateRelationshipType,
  useDeleteRelationshipType,
  useContactSources,
  useCreateContactSource,
  useUpdateContactSource,
  useDeleteContactSource,
  useProjectRequestStatuses,
  useCreateProjectRequestStatus,
  useUpdateProjectRequestStatus,
  useDeleteProjectRequestStatus,
  useLexwareConfig,
  useSetLexwareConfig,
  useClockodoSettings,
  useUpdateClockodoSettings,
  useTestClockodoConnection,
} from './hooks';
import type {
  Industry,
  AccountType,
  RelationshipType,
  ContactSourceType,
  ProjectRequestStatus,
} from './types';

// ============================================================
//  Styles
// ============================================================

const cardStyle: React.CSSProperties = {
  background: 'var(--color-bg-card)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-sm)',
  border: '1px solid var(--color-border)',
  overflow: 'hidden',
};

const cardHeaderStyle: React.CSSProperties = {
  padding: '1rem 1.25rem',
  borderBottom: '1px solid var(--color-border)',
  background: 'var(--color-bg)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.8125rem',
};

const thStyle: React.CSSProperties = {
  padding: '0.625rem 1rem',
  textAlign: 'left',
  fontSize: '0.6875rem',
  textTransform: 'uppercase',
  color: 'var(--color-text-muted)',
  fontWeight: 500,
  borderBottom: '1px solid var(--color-border)',
};

const tdStyle: React.CSSProperties = {
  padding: '0.625rem 1rem',
  borderBottom: '1px solid var(--color-border)',
  color: 'var(--color-text)',
};

const inputStyle: React.CSSProperties = {
  padding: '0.375rem 0.625rem',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: '0.8125rem',
  background: 'var(--color-bg-card)',
  color: 'var(--color-text)',
  outline: 'none',
  boxSizing: 'border-box',
};

const btnSmall: React.CSSProperties = {
  padding: '0.25rem 0.5rem',
  fontSize: '0.75rem',
  fontWeight: 500,
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--color-border)',
  background: 'transparent',
  cursor: 'pointer',
  color: 'var(--color-text-secondary)',
};

const btnPrimary: React.CSSProperties = {
  padding: '0.375rem 0.75rem',
  fontSize: '0.8125rem',
  fontWeight: 500,
  borderRadius: 'var(--radius-sm)',
  border: 'none',
  background: 'var(--color-primary)',
  color: 'white',
  cursor: 'pointer',
};

const btnDanger: React.CSSProperties = {
  ...btnSmall,
  borderColor: '#fecaca',
  color: '#dc2626',
};

// ============================================================
//  Main Component — Integrationen (Lexware, UV)
// ============================================================

export function CrmSettingsTab() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div>
        <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 0.25rem' }}>
          Integrationen
        </h3>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', margin: 0 }}>
          API-Keys und Zugangsdaten fuer externe Dienste verwalten.
        </p>
      </div>

      <LexwareApiKeyCard />
      <ClockodoApiCard />
      <UvCredentialsCard />
    </div>
  );
}

// ============================================================
//  CRM Stammdaten — exportiert fuer CrmModulePage
// ============================================================

type MasterDataTab = 'accountTypes' | 'industries' | 'contactSources' | 'relationshipTypes' | 'projectRequestStatuses';

const MASTER_DATA_TABS: { key: MasterDataTab; label: string }[] = [
  { key: 'accountTypes', label: 'Kontotypen' },
  { key: 'industries', label: 'Branchen' },
  { key: 'contactSources', label: 'Kontaktquellen' },
  { key: 'relationshipTypes', label: 'Beziehungstypen' },
  { key: 'projectRequestStatuses', label: 'Projektanfrage-Status' },
];

export function CrmMasterDataCards() {
  const [activeTab, setActiveTab] = useState<MasterDataTab>('accountTypes');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div>
        <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 0.25rem' }}>
          CRM Stammdaten
        </h3>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', margin: 0 }}>
          Kontotypen, Branchen, Kontaktquellen und weitere CRM-Listen verwalten.
        </p>
      </div>

      {/* Tab-Leiste */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid var(--color-border)' }}>
        {MASTER_DATA_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '0.625rem 1.25rem',
              fontSize: '0.8125rem',
              fontWeight: activeTab === tab.key ? 600 : 400,
              color: activeTab === tab.key ? 'var(--color-primary)' : 'var(--color-text-secondary)',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab.key ? '2px solid var(--color-primary)' : '2px solid transparent',
              marginBottom: '-2px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab-Inhalt */}
      {activeTab === 'accountTypes' && <AccountTypesCard />}
      {activeTab === 'industries' && <IndustriesCard />}
      {activeTab === 'contactSources' && <ContactSourcesCard />}
      {activeTab === 'relationshipTypes' && <RelationshipTypesCard />}
      {activeTab === 'projectRequestStatuses' && <ProjectRequestStatusesCard />}
    </div>
  );
}

// ============================================================
//  Kontotypen
// ============================================================

function AccountTypesCard() {
  const { data, isLoading } = useAccountTypes();
  const createMut = useCreateAccountType();
  const updateMut = useUpdateAccountType();
  const deleteMut = useDeleteAccountType();

  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const items: AccountType[] = (data?.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name, 'de'));

  const handleCreate = () => {
    if (!newName.trim()) return;
    createMut.mutate(
      { name: newName.trim(), sortOrder: items.length + 1 },
      {
        onSuccess: () => {
          setNewName('');
          setIsAdding(false);
        },
      },
    );
  };

  const handleUpdate = (id: string) => {
    if (!editName.trim()) return;
    updateMut.mutate(
      { id, data: { name: editName.trim() } },
      { onSuccess: () => setEditingId(null) },
    );
  };

  const handleDelete = (id: string, name: string) => {
    if (window.confirm(`Kontotyp "${name}" wirklich löschen?`)) {
      deleteMut.mutate(id);
    }
  };

  return (
    <div style={cardStyle}>
      <div style={cardHeaderStyle}>
        <div>
          <h4 style={{ fontSize: '0.9375rem', fontWeight: 600, margin: 0 }}>
            Kontotypen
          </h4>
          <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', margin: '0.125rem 0 0' }}>
            z.B. Interessent, Endkunde, Partner
          </p>
        </div>
        <button style={btnPrimary} onClick={() => setIsAdding(true)}>
          + Neu
        </button>
      </div>

      {isLoading ? (
        <p style={{ padding: '1rem', color: 'var(--color-text-muted)', fontSize: '0.8125rem' }}>Laden...</p>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Name</th>
              <th style={{ ...thStyle, width: 80, textAlign: 'center' }}>Unternehmen</th>
              <th style={{ ...thStyle, width: 120, textAlign: 'right' }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {isAdding && (
              <tr>
                <td style={tdStyle}>
                  <input
                    style={{ ...inputStyle, width: '100%' }}
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Neuer Kontotyp..."
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                  />
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>—</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                    <button style={btnSmall} onClick={handleCreate} disabled={createMut.isPending}>
                      {createMut.isPending ? '...' : 'Speichern'}
                    </button>
                    <button style={btnSmall} onClick={() => { setIsAdding(false); setNewName(''); }}>
                      Abbrechen
                    </button>
                  </div>
                </td>
              </tr>
            )}
            {items.map((item) => (
              <tr key={item.id}>
                <td style={tdStyle}>
                  {editingId === item.id ? (
                    <input
                      style={{ ...inputStyle, width: '100%' }}
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleUpdate(item.id);
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                    />
                  ) : (
                    <span style={{ fontWeight: 500 }}>{item.name}</span>
                  )}
                </td>
                <td style={{ ...tdStyle, textAlign: 'center', color: 'var(--color-text-muted)' }}>
                  {item._count?.companies ?? 0}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                    {editingId === item.id ? (
                      <>
                        <button style={btnSmall} onClick={() => handleUpdate(item.id)} disabled={updateMut.isPending}>
                          {updateMut.isPending ? '...' : 'Speichern'}
                        </button>
                        <button style={btnSmall} onClick={() => setEditingId(null)}>Abbrechen</button>
                      </>
                    ) : (
                      <>
                        <button style={btnSmall} onClick={() => { setEditingId(item.id); setEditName(item.name); }}>
                          Bearbeiten
                        </button>
                        <button style={btnDanger} onClick={() => handleDelete(item.id, item.name)}>
                          Löschen
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && !isAdding && (
              <tr>
                <td colSpan={3} style={{ ...tdStyle, textAlign: 'center', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                  Keine Kontotypen vorhanden
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ============================================================
//  Branchen
// ============================================================

function IndustriesCard() {
  const { data, isLoading } = useIndustries();
  const createMut = useCreateIndustry();
  const updateMut = useUpdateIndustry();
  const deleteMut = useDeleteIndustry();

  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#3B82F6');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');

  const items: Industry[] = (data?.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name, 'de'));

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6366F1', '#14B8A6', '#F97316', '#6B7280'];

  const handleCreate = () => {
    if (!newName.trim()) return;
    createMut.mutate(
      { name: newName.trim(), color: newColor, sortOrder: items.length + 1 },
      {
        onSuccess: () => {
          setNewName('');
          setNewColor('#3B82F6');
          setIsAdding(false);
        },
      },
    );
  };

  const handleUpdate = (id: string) => {
    if (!editName.trim()) return;
    updateMut.mutate(
      { id, data: { name: editName.trim(), color: editColor } },
      { onSuccess: () => setEditingId(null) },
    );
  };

  const handleDelete = (id: string, name: string) => {
    if (window.confirm(`Branche "${name}" wirklich löschen?`)) {
      deleteMut.mutate(id);
    }
  };

  return (
    <div style={cardStyle}>
      <div style={cardHeaderStyle}>
        <div>
          <h4 style={{ fontSize: '0.9375rem', fontWeight: 600, margin: 0 }}>
            Branchen
          </h4>
          <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', margin: '0.125rem 0 0' }}>
            z.B. IT & Software, Produktion, Handel
          </p>
        </div>
        <button style={btnPrimary} onClick={() => setIsAdding(true)}>
          + Neu
        </button>
      </div>

      {isLoading ? (
        <p style={{ padding: '1rem', color: 'var(--color-text-muted)', fontSize: '0.8125rem' }}>Laden...</p>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Name</th>
              <th style={{ ...thStyle, width: 60 }}>Farbe</th>
              <th style={{ ...thStyle, width: 80, textAlign: 'center' }}>Unternehmen</th>
              <th style={{ ...thStyle, width: 120, textAlign: 'right' }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {isAdding && (
              <tr>
                <td style={tdStyle}>
                  <input
                    style={{ ...inputStyle, width: '100%' }}
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Neue Branche..."
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                  />
                </td>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                    {COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setNewColor(c)}
                        style={{
                          width: 18, height: 18, borderRadius: '50%',
                          background: c,
                          border: newColor === c ? '2px solid var(--color-text)' : '2px solid transparent',
                          cursor: 'pointer', padding: 0,
                        }}
                      />
                    ))}
                  </div>
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>—</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                    <button style={btnSmall} onClick={handleCreate} disabled={createMut.isPending}>
                      {createMut.isPending ? '...' : 'Speichern'}
                    </button>
                    <button style={btnSmall} onClick={() => { setIsAdding(false); setNewName(''); }}>
                      Abbrechen
                    </button>
                  </div>
                </td>
              </tr>
            )}
            {items.map((item) => (
              <tr key={item.id}>
                <td style={tdStyle}>
                  {editingId === item.id ? (
                    <input
                      style={{ ...inputStyle, width: '100%' }}
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleUpdate(item.id);
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                    />
                  ) : (
                    <span style={{ fontWeight: 500 }}>{item.name}</span>
                  )}
                </td>
                <td style={tdStyle}>
                  {editingId === item.id ? (
                    <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                      {COLORS.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setEditColor(c)}
                          style={{
                            width: 18, height: 18, borderRadius: '50%',
                            background: c,
                            border: editColor === c ? '2px solid var(--color-text)' : '2px solid transparent',
                            cursor: 'pointer', padding: 0,
                          }}
                        />
                      ))}
                    </div>
                  ) : (
                    <span
                      style={{
                        display: 'inline-block', width: 18, height: 18,
                        borderRadius: '50%', background: item.color,
                      }}
                    />
                  )}
                </td>
                <td style={{ ...tdStyle, textAlign: 'center', color: 'var(--color-text-muted)' }}>
                  {item._count?.companies ?? 0}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                    {editingId === item.id ? (
                      <>
                        <button style={btnSmall} onClick={() => handleUpdate(item.id)} disabled={updateMut.isPending}>
                          {updateMut.isPending ? '...' : 'Speichern'}
                        </button>
                        <button style={btnSmall} onClick={() => setEditingId(null)}>Abbrechen</button>
                      </>
                    ) : (
                      <>
                        <button style={btnSmall} onClick={() => { setEditingId(item.id); setEditName(item.name); setEditColor(item.color); }}>
                          Bearbeiten
                        </button>
                        <button style={btnDanger} onClick={() => handleDelete(item.id, item.name)}>
                          Löschen
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && !isAdding && (
              <tr>
                <td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                  Keine Branchen vorhanden
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ============================================================
//  Kontaktquellen
// ============================================================

function ContactSourcesCard() {
  const { data, isLoading } = useContactSources();
  const createMut = useCreateContactSource();
  const updateMut = useUpdateContactSource();
  const deleteMut = useDeleteContactSource();

  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const items: ContactSourceType[] = (data?.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name, 'de'));

  const handleCreate = () => {
    if (!newName.trim()) return;
    createMut.mutate(
      { name: newName.trim(), sortOrder: items.length + 1 },
      {
        onSuccess: () => {
          setNewName('');
          setIsAdding(false);
        },
      },
    );
  };

  const handleUpdate = (id: string) => {
    if (!editName.trim()) return;
    updateMut.mutate(
      { id, data: { name: editName.trim() } },
      { onSuccess: () => setEditingId(null) },
    );
  };

  const handleDelete = (id: string, name: string) => {
    if (window.confirm(`Kontaktquelle "${name}" wirklich löschen?`)) {
      deleteMut.mutate(id);
    }
  };

  return (
    <div style={cardStyle}>
      <div style={cardHeaderStyle}>
        <div>
          <h4 style={{ fontSize: '0.9375rem', fontWeight: 600, margin: 0 }}>
            Kontaktquellen
          </h4>
          <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', margin: '0.125rem 0 0' }}>
            z.B. Messe, Empfehlung, Website, Kaltakquise
          </p>
        </div>
        <button style={btnPrimary} onClick={() => setIsAdding(true)}>
          + Neu
        </button>
      </div>

      {isLoading ? (
        <p style={{ padding: '1rem', color: 'var(--color-text-muted)', fontSize: '0.8125rem' }}>Laden...</p>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Name</th>
              <th style={{ ...thStyle, width: 120, textAlign: 'right' }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {isAdding && (
              <tr>
                <td style={tdStyle}>
                  <input
                    style={{ ...inputStyle, width: '100%' }}
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Neue Kontaktquelle..."
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                  />
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                    <button style={btnSmall} onClick={handleCreate} disabled={createMut.isPending}>
                      {createMut.isPending ? '...' : 'Speichern'}
                    </button>
                    <button style={btnSmall} onClick={() => { setIsAdding(false); setNewName(''); }}>
                      Abbrechen
                    </button>
                  </div>
                </td>
              </tr>
            )}
            {items.map((item) => (
              <tr key={item.id}>
                <td style={tdStyle}>
                  {editingId === item.id ? (
                    <input
                      style={{ ...inputStyle, width: '100%' }}
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleUpdate(item.id);
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                    />
                  ) : (
                    <span style={{ fontWeight: 500 }}>{item.name}</span>
                  )}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                    {editingId === item.id ? (
                      <>
                        <button style={btnSmall} onClick={() => handleUpdate(item.id)} disabled={updateMut.isPending}>
                          {updateMut.isPending ? '...' : 'Speichern'}
                        </button>
                        <button style={btnSmall} onClick={() => setEditingId(null)}>Abbrechen</button>
                      </>
                    ) : (
                      <>
                        <button style={btnSmall} onClick={() => { setEditingId(item.id); setEditName(item.name); }}>
                          Bearbeiten
                        </button>
                        <button style={btnDanger} onClick={() => handleDelete(item.id, item.name)}>
                          Löschen
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && !isAdding && (
              <tr>
                <td colSpan={2} style={{ ...tdStyle, textAlign: 'center', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                  Keine Kontaktquellen vorhanden
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ============================================================
//  Beziehungstypen
// ============================================================

function RelationshipTypesCard() {
  const { data, isLoading } = useRelationshipTypes();
  const createMut = useCreateRelationshipType();
  const updateMut = useUpdateRelationshipType();
  const deleteMut = useDeleteRelationshipType();

  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const items: RelationshipType[] = (data?.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name, 'de'));

  const handleCreate = () => {
    if (!newName.trim()) return;
    createMut.mutate(
      { name: newName.trim(), sortOrder: items.length + 1 },
      {
        onSuccess: () => {
          setNewName('');
          setIsAdding(false);
        },
      },
    );
  };

  const handleUpdate = (id: string) => {
    if (!editName.trim()) return;
    updateMut.mutate(
      { id, data: { name: editName.trim() } },
      { onSuccess: () => setEditingId(null) },
    );
  };

  const handleDelete = (id: string, name: string) => {
    if (window.confirm(`Beziehungstyp "${name}" wirklich löschen?`)) {
      deleteMut.mutate(id);
    }
  };

  return (
    <div style={cardStyle}>
      <div style={cardHeaderStyle}>
        <div>
          <h4 style={{ fontSize: '0.9375rem', fontWeight: 600, margin: 0 }}>
            Beziehungstypen
          </h4>
          <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', margin: '0.125rem 0 0' }}>
            z.B. Muttergesellschaft, Tochtergesellschaft, Partner
          </p>
        </div>
        <button style={btnPrimary} onClick={() => setIsAdding(true)}>
          + Neu
        </button>
      </div>

      {isLoading ? (
        <p style={{ padding: '1rem', color: 'var(--color-text-muted)', fontSize: '0.8125rem' }}>Laden...</p>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Name</th>
              <th style={{ ...thStyle, width: 120, textAlign: 'right' }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {isAdding && (
              <tr>
                <td style={tdStyle}>
                  <input
                    style={{ ...inputStyle, width: '100%' }}
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Neuer Beziehungstyp..."
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                  />
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                    <button style={btnSmall} onClick={handleCreate} disabled={createMut.isPending}>
                      {createMut.isPending ? '...' : 'Speichern'}
                    </button>
                    <button style={btnSmall} onClick={() => { setIsAdding(false); setNewName(''); }}>
                      Abbrechen
                    </button>
                  </div>
                </td>
              </tr>
            )}
            {items.map((item) => (
              <tr key={item.id}>
                <td style={tdStyle}>
                  {editingId === item.id ? (
                    <input
                      style={{ ...inputStyle, width: '100%' }}
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleUpdate(item.id);
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                    />
                  ) : (
                    <span style={{ fontWeight: 500 }}>{item.name}</span>
                  )}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                    {editingId === item.id ? (
                      <>
                        <button style={btnSmall} onClick={() => handleUpdate(item.id)} disabled={updateMut.isPending}>
                          {updateMut.isPending ? '...' : 'Speichern'}
                        </button>
                        <button style={btnSmall} onClick={() => setEditingId(null)}>Abbrechen</button>
                      </>
                    ) : (
                      <>
                        <button style={btnSmall} onClick={() => { setEditingId(item.id); setEditName(item.name); }}>
                          Bearbeiten
                        </button>
                        <button style={btnDanger} onClick={() => handleDelete(item.id, item.name)}>
                          Löschen
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && !isAdding && (
              <tr>
                <td colSpan={2} style={{ ...tdStyle, textAlign: 'center', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                  Keine Beziehungstypen vorhanden
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ============================================================
//  Projektanfrage-Status
// ============================================================

function ProjectRequestStatusesCard() {
  const { data, isLoading } = useProjectRequestStatuses();
  const createMut = useCreateProjectRequestStatus();
  const updateMut = useUpdateProjectRequestStatus();
  const deleteMut = useDeleteProjectRequestStatus();

  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#6B7280');
  const [newPhase, setNewPhase] = useState<'INQUIRY' | 'ACTIVE' | 'COMPLETED'>('INQUIRY');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [editPhase, setEditPhase] = useState<'INQUIRY' | 'ACTIVE' | 'COMPLETED'>('INQUIRY');

  const PHASE_LABELS: Record<'INQUIRY' | 'ACTIVE' | 'COMPLETED', string> = {
    INQUIRY: 'Anfragen',
    ACTIVE: 'Aktuell',
    COMPLETED: 'Abgeschlossen',
  };

  const items: ProjectRequestStatus[] = (data?.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name, 'de'));

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6366F1', '#14B8A6', '#F97316', '#6B7280'];

  const handleCreate = () => {
    if (!newName.trim()) return;
    createMut.mutate(
      { name: newName.trim(), color: newColor, phase: newPhase, sortOrder: items.length + 1 },
      {
        onSuccess: () => {
          setNewName('');
          setNewColor('#6B7280');
          setNewPhase('INQUIRY');
          setIsAdding(false);
        },
      },
    );
  };

  const handleUpdate = (id: string) => {
    if (!editName.trim()) return;
    updateMut.mutate(
      { id, data: { name: editName.trim(), color: editColor, phase: editPhase } },
      { onSuccess: () => setEditingId(null) },
    );
  };

  const handleDelete = (id: string, name: string) => {
    if (window.confirm(`Projektanfrage-Status "${name}" wirklich löschen?`)) {
      deleteMut.mutate(id);
    }
  };

  return (
    <div style={cardStyle}>
      <div style={cardHeaderStyle}>
        <div>
          <h4 style={{ fontSize: '0.9375rem', fontWeight: 600, margin: 0 }}>
            Projektanfrage-Status
          </h4>
          <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', margin: '0.125rem 0 0' }}>
            z.B. Offen, In Bearbeitung, Angebot erstellt, Abgeschlossen
          </p>
        </div>
        <button style={btnPrimary} onClick={() => setIsAdding(true)}>
          + Neu
        </button>
      </div>

      {isLoading ? (
        <p style={{ padding: '1rem', color: 'var(--color-text-muted)', fontSize: '0.8125rem' }}>Laden...</p>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Name</th>
              <th style={{ ...thStyle, width: 60 }}>Farbe</th>
              <th style={{ ...thStyle, width: 130 }}>Reiter</th>
              <th style={{ ...thStyle, width: 120, textAlign: 'right' }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {isAdding && (
              <tr>
                <td style={tdStyle}>
                  <input
                    style={{ ...inputStyle, width: '100%' }}
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Neuer Status..."
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                  />
                </td>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                    {COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setNewColor(c)}
                        style={{
                          width: 18, height: 18, borderRadius: '50%',
                          background: c,
                          border: newColor === c ? '2px solid var(--color-text)' : '2px solid transparent',
                          cursor: 'pointer', padding: 0,
                        }}
                      />
                    ))}
                  </div>
                </td>
                <td style={tdStyle}>
                  <select
                    value={newPhase}
                    onChange={(e) => setNewPhase(e.target.value as 'INQUIRY' | 'ACTIVE' | 'COMPLETED')}
                    style={{ ...inputStyle, fontSize: '0.8125rem', height: 30, padding: '0.25rem 0.375rem' }}
                  >
                    {(Object.keys(PHASE_LABELS) as Array<'INQUIRY' | 'ACTIVE' | 'COMPLETED'>).map((p) => (
                      <option key={p} value={p}>{PHASE_LABELS[p]}</option>
                    ))}
                  </select>
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                    <button style={btnSmall} onClick={handleCreate} disabled={createMut.isPending}>
                      {createMut.isPending ? '...' : 'Speichern'}
                    </button>
                    <button style={btnSmall} onClick={() => { setIsAdding(false); setNewName(''); }}>
                      Abbrechen
                    </button>
                  </div>
                </td>
              </tr>
            )}
            {items.map((item) => (
              <tr key={item.id}>
                <td style={tdStyle}>
                  {editingId === item.id ? (
                    <input
                      style={{ ...inputStyle, width: '100%' }}
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleUpdate(item.id);
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                    />
                  ) : (
                    <span style={{ fontWeight: 500 }}>{item.name}</span>
                  )}
                </td>
                <td style={tdStyle}>
                  {editingId === item.id ? (
                    <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                      {COLORS.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setEditColor(c)}
                          style={{
                            width: 18, height: 18, borderRadius: '50%',
                            background: c,
                            border: editColor === c ? '2px solid var(--color-text)' : '2px solid transparent',
                            cursor: 'pointer', padding: 0,
                          }}
                        />
                      ))}
                    </div>
                  ) : (
                    <span
                      style={{
                        display: 'inline-block', width: 18, height: 18,
                        borderRadius: '50%', background: item.color,
                      }}
                    />
                  )}
                </td>
                <td style={tdStyle}>
                  {editingId === item.id ? (
                    <select
                      value={editPhase}
                      onChange={(e) => setEditPhase(e.target.value as 'INQUIRY' | 'ACTIVE' | 'COMPLETED')}
                      style={{ ...inputStyle, fontSize: '0.8125rem', height: 30, padding: '0.25rem 0.375rem' }}
                    >
                      {(Object.keys(PHASE_LABELS) as Array<'INQUIRY' | 'ACTIVE' | 'COMPLETED'>).map((p) => (
                        <option key={p} value={p}>{PHASE_LABELS[p]}</option>
                      ))}
                    </select>
                  ) : (
                    <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                      {PHASE_LABELS[item.phase ?? 'INQUIRY']}
                    </span>
                  )}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                    {editingId === item.id ? (
                      <>
                        <button style={btnSmall} onClick={() => handleUpdate(item.id)} disabled={updateMut.isPending}>
                          {updateMut.isPending ? '...' : 'Speichern'}
                        </button>
                        <button style={btnSmall} onClick={() => setEditingId(null)}>Abbrechen</button>
                      </>
                    ) : (
                      <>
                        <button style={btnSmall} onClick={() => { setEditingId(item.id); setEditName(item.name); setEditColor(item.color); setEditPhase(item.phase ?? 'INQUIRY'); }}>
                          Bearbeiten
                        </button>
                        <button style={btnDanger} onClick={() => handleDelete(item.id, item.name)}>
                          Löschen
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && !isAdding && (
              <tr>
                <td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                  Keine Projektanfrage-Status vorhanden
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ============================================================
//  Clockodo API
// ============================================================

function ClockodoApiCard() {
  const { data, isLoading } = useClockodoSettings();
  const updateSettings = useUpdateClockodoSettings();
  const testConnection = useTestClockodoConnection();
  const [apiUser, setApiUser] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [editing, setEditing] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const config = data?.data;
  const configured = config?.configured ?? config?.enabled ?? false;

  if (isLoading) return null;

  return (
    <div style={cardStyle}>
      <div style={cardHeaderStyle}>
        <div>
          <h4 style={{ fontSize: '0.9375rem', fontWeight: 600, margin: 0 }}>
            Clockodo
          </h4>
          <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', margin: '0.125rem 0 0' }}>
            Zeiterfassung & Projektzeiten aus Clockodo
          </p>
        </div>
        {configured && (
          <span style={{
            fontSize: '0.6875rem', fontWeight: 600,
            padding: '0.125rem 0.5rem', borderRadius: '999px',
            background: '#dcfce7', color: '#16a34a',
          }}>
            Konfiguriert
          </span>
        )}
      </div>

      <div style={{ padding: '1rem' }}>
        {/* Einrichtungsanleitung */}
        <div style={{
          background: 'var(--color-bg)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-sm)',
          padding: '0.75rem 1rem',
          fontSize: '0.75rem',
          color: 'var(--color-text-secondary)',
          lineHeight: 1.6,
          marginBottom: '1rem',
        }}>
          <strong style={{ color: 'var(--color-text)', display: 'block', marginBottom: '0.25rem' }}>
            Einrichtung (auch bei Microsoft SSO):
          </strong>
          <ol style={{ margin: 0, paddingLeft: '1.25rem' }}>
            <li>In Clockodo einloggen (via Microsoft SSO wie gewohnt)</li>
            <li>Oben rechts auf dein <strong>Profil-Icon</strong> klicken &rarr; <strong>Mein Profil</strong></li>
            <li>Zum Bereich <strong>API</strong> scrollen</li>
            <li>Die angezeigte <strong>E-Mail-Adresse</strong> und den <strong>API-Key</strong> kopieren</li>
            <li>Hier unten einfügen und speichern</li>
          </ol>
          <p style={{ margin: '0.375rem 0 0', fontSize: '0.6875rem', color: 'var(--color-text-muted)' }}>
            Hinweis: Der API-Key ist unabh&auml;ngig vom SSO-Login.
          </p>
        </div>

        {configured && config?.apiUser && (
          <div style={{
            marginBottom: '0.75rem',
            fontSize: '0.8125rem',
            color: 'var(--color-text-muted)',
          }}>
            API-User: <code style={{
              background: 'var(--color-bg)',
              padding: '0.125rem 0.375rem',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.75rem',
            }}>{config.apiUser}</code>
            {config?.apiKey && (
              <> &middot; Key: <code style={{
                background: 'var(--color-bg)',
                padding: '0.125rem 0.375rem',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.75rem',
              }}>{config.apiKey}</code></>
            )}
          </div>
        )}

        {editing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div>
              <label style={{
                fontSize: '0.75rem', fontWeight: 600,
                color: 'var(--color-text-muted)', display: 'block',
                marginBottom: '0.25rem',
              }}>
                API-Benutzer (E-Mail)
              </label>
              <input
                type="email"
                value={apiUser}
                onChange={(e) => setApiUser(e.target.value)}
                placeholder="user@example.com"
                style={{ ...inputStyle, width: '100%', maxWidth: '480px' }}
              />
            </div>
            <div>
              <label style={{
                fontSize: '0.75rem', fontWeight: 600,
                color: 'var(--color-text-muted)', display: 'block',
                marginBottom: '0.25rem',
              }}>
                API-Key
              </label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Clockodo API Key eingeben"
                style={{ ...inputStyle, width: '100%', maxWidth: '480px' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  if (!apiUser.trim() || !apiKey.trim()) return;
                  updateSettings.mutate(
                    { apiUser: apiUser.trim(), apiKey: apiKey.trim() },
                    {
                      onSuccess: () => {
                        setApiUser('');
                        setApiKey('');
                        setEditing(false);
                        setTestResult(null);
                      },
                    },
                  );
                }}
                disabled={!apiUser.trim() || !apiKey.trim() || updateSettings.isPending}
                style={{
                  ...btnPrimary,
                  opacity: !apiUser.trim() || !apiKey.trim() || updateSettings.isPending ? 0.5 : 1,
                  cursor: updateSettings.isPending ? 'wait' : 'pointer',
                }}
              >
                {updateSettings.isPending ? 'Speichern...' : 'Zugangsdaten speichern'}
              </button>
              <button
                onClick={() => {
                  setTestResult(null);
                  testConnection.mutate(undefined, {
                    onSuccess: (res: any) => {
                      const d = res?.data ?? res;
                      setTestResult({ success: d?.success ?? true, message: d?.message ?? 'Verbindung erfolgreich!' });
                    },
                    onError: (err: any) => {
                      setTestResult({ success: false, message: err?.message ?? 'Verbindung fehlgeschlagen' });
                    },
                  });
                }}
                disabled={testConnection.isPending}
                style={{
                  ...btnSmall,
                  cursor: testConnection.isPending ? 'wait' : 'pointer',
                }}
              >
                {testConnection.isPending ? 'Teste...' : 'Verbindung testen'}
              </button>
              <button
                onClick={() => { setEditing(false); setApiUser(''); setApiKey(''); setTestResult(null); }}
                style={btnSmall}
              >
                Abbrechen
              </button>
            </div>
            {testResult && (
              <span style={{
                fontSize: '0.75rem', fontWeight: 500,
                color: testResult.success ? '#16a34a' : 'var(--color-error, #dc2626)',
              }}>
                {testResult.message}
              </span>
            )}
            {updateSettings.isError && (
              <span style={{ fontSize: '0.75rem', color: 'var(--color-error)', fontWeight: 500 }}>
                Fehler beim Speichern
              </span>
            )}
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            style={btnPrimary}
          >
            {configured ? 'Zugangsdaten ändern' : 'Zugangsdaten eingeben'}
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================
//  Lexware Office API-Key
// ============================================================

function LexwareApiKeyCard() {
  const { data, isLoading } = useLexwareConfig();
  const setConfig = useSetLexwareConfig();
  const [apiKey, setApiKey] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const config = data?.data;
  const configured = config?.configured ?? false;

  const handleSave = () => {
    if (!apiKey.trim()) return;
    setStatus('idle');
    setConfig.mutate(apiKey.trim(), {
      onSuccess: () => {
        setApiKey('');
        setStatus('success');
        setTimeout(() => setStatus('idle'), 3000);
      },
      onError: () => {
        setStatus('error');
      },
    });
  };

  if (isLoading) return null;

  return (
    <div style={cardStyle}>
      <div style={cardHeaderStyle}>
        <div>
          <h4 style={{ fontSize: '0.9375rem', fontWeight: 600, margin: 0 }}>
            Lexware Office
          </h4>
          <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', margin: '0.125rem 0 0' }}>
            API-Key für die Lexware Office ERP-Integration (Rechnungen, Angebote, Kontakte)
          </p>
        </div>
        {configured && (
          <span style={{
            fontSize: '0.6875rem', fontWeight: 600,
            padding: '0.125rem 0.5rem', borderRadius: '999px',
            background: '#dcfce7', color: '#16a34a',
          }}>
            Konfiguriert
          </span>
        )}
      </div>

      <div style={{ padding: '1rem' }}>
        {configured && config?.apiKey && (
          <div style={{
            marginBottom: '0.75rem',
            fontSize: '0.8125rem',
            color: 'var(--color-text-muted)',
          }}>
            Aktueller Key: <code style={{
              background: 'var(--color-bg)',
              padding: '0.125rem 0.375rem',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.75rem',
            }}>{config.apiKey}</code>
          </div>
        )}

        <div style={{ marginBottom: '0.75rem' }}>
          <label style={{
            fontSize: '0.75rem', fontWeight: 600,
            color: 'var(--color-text-muted)', display: 'block',
            marginBottom: '0.25rem',
          }}>
            {configured ? 'Neuen API-Key eingeben' : 'API-Key'}
          </label>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={configured ? 'Neuen API-Key eingeben...' : 'Lexware Office API-Key eingeben...'}
            style={{ ...inputStyle, width: '100%', maxWidth: '480px' }}
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
          />
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            onClick={handleSave}
            disabled={setConfig.isPending || !apiKey.trim()}
            style={{
              ...btnPrimary,
              opacity: setConfig.isPending || !apiKey.trim() ? 0.5 : 1,
              cursor: setConfig.isPending ? 'wait' : 'pointer',
            }}
          >
            {setConfig.isPending ? 'Speichern...' : 'API-Key speichern'}
          </button>
          {status === 'success' && (
            <span style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 500 }}>
              API-Key gespeichert
            </span>
          )}
          {status === 'error' && (
            <span style={{ fontSize: '0.75rem', color: 'var(--color-error)', fontWeight: 500 }}>
              Fehler beim Speichern
            </span>
          )}
        </div>

        {!configured && (
          <p style={{
            marginTop: '0.75rem', fontSize: '0.75rem',
            color: 'var(--color-text-muted)', lineHeight: 1.5,
          }}>
            Den API-Key finden Sie in Ihrem Lexware Office Konto unter Einstellungen &rarr; Schnittstellen &rarr; Public API.
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================================
//  Unternehmensverzeichnis API-Zugangsdaten
// ============================================================

function UvCredentialsCard() {
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [configured, setConfigured] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Load current status on mount
  useState(() => {
    import('./api').then(({ uvApi }) => {
      uvApi.getCredentials().then((res) => {
        if (res.data.configured) {
          setUser(res.data.user);
          setConfigured(true);
        }
        setLoaded(true);
      }).catch(() => setLoaded(true));
    });
  });

  const handleSave = async () => {
    if (!user.trim() || !password.trim()) return;
    setSaving(true);
    setStatus('idle');
    try {
      const { uvApi } = await import('./api');
      await uvApi.saveCredentials(user.trim(), password.trim());
      setConfigured(true);
      setPassword('');
      setStatus('success');
    } catch {
      setStatus('error');
    }
    setSaving(false);
  };

  const handleCheck = async () => {
    setChecking(true);
    setStatus('idle');
    try {
      const { uvApi } = await import('./api');
      const res = await uvApi.checkAuth();
      setStatus(res.data.authenticated ? 'success' : 'error');
    } catch {
      setStatus('error');
    }
    setChecking(false);
  };

  if (!loaded) return null;

  return (
    <div style={cardStyle}>
      <div style={cardHeaderStyle}>
        <div>
          <h4 style={{ fontSize: '0.9375rem', fontWeight: 600, margin: 0 }}>
            Unternehmensverzeichnis.org
          </h4>
          <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', margin: '0.125rem 0 0' }}>
            API-Zugangsdaten für die Datenanreicherung (EBID)
          </p>
        </div>
        {configured && (
          <span style={{
            fontSize: '0.6875rem', fontWeight: 600,
            padding: '0.125rem 0.5rem', borderRadius: '999px',
            background: '#dcfce7', color: '#16a34a',
          }}>
            Konfiguriert
          </span>
        )}
      </div>

      <div style={{ padding: '1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <div>
            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.25rem' }}>
              Benutzername (E-Mail)
            </label>
            <input
              type="email"
              value={user}
              onChange={(e) => setUser(e.target.value)}
              placeholder="api@firma.de"
              style={{ ...inputStyle, width: '100%' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.25rem' }}>
              Passwort
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={configured ? '••••••••' : 'Passwort'}
              style={{ ...inputStyle, width: '100%' }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            onClick={handleSave}
            disabled={saving || !user.trim() || !password.trim()}
            style={{
              ...btnPrimary,
              opacity: saving || !user.trim() || !password.trim() ? 0.5 : 1,
              cursor: saving ? 'wait' : 'pointer',
            }}
          >
            {saving ? 'Speichern...' : 'Zugangsdaten speichern'}
          </button>
          {configured && (
            <button
              onClick={handleCheck}
              disabled={checking}
              style={{
                ...btnSmall,
                cursor: checking ? 'wait' : 'pointer',
              }}
            >
              {checking ? 'Prüfen...' : 'Verbindung testen'}
            </button>
          )}
          {status === 'success' && (
            <span style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 500 }}>
              ✓ Verbindung erfolgreich
            </span>
          )}
          {status === 'error' && (
            <span style={{ fontSize: '0.75rem', color: 'var(--color-error)', fontWeight: 500 }}>
              ✕ Verbindung fehlgeschlagen
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
